import React, { useMemo } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { ListSidebar, MainLayout } from "layouts";
import { HomePage } from "./HomePage";
import { SystemLogsPage } from "./SystemLogsPage";
import { McpLibraryPage } from "./McpLibraryPage";
import { MemberLibraryPageRouter } from "./MemberLibraryPage";
import { ConsumptionPage } from "./ConsumptionPage";
import { QueueHealthPage } from "./QueueHealthPage";
import { McpAuditPage } from "./McpAuditPage";
import { DeadLetterJobsPage } from "./DeadLetterJobsPage";
import { CompensationsPage } from "./CompensationsPage";
import { KnowledgeBaseBackofficePage } from "./KnowledgeBasePage";
import { DevBotProposalsPage } from "./DevBotProposalsPage";
import { PromptsPage } from "./PromptsPage";
import { Error404Page } from "pages/Error404Page";
import { Link } from "components";
import { clsx } from "@ismael1361/utils";
import { Divider } from "@mui/material";
import Icon from "@mdi/react";
import { mdiViewDashboard, mdiAccountCog, mdiConsole, mdiScriptTextOutline, mdiPuzzleOutline, mdiCurrencyUsd, mdiLifebuoy, mdiLogout, mdiRobotOutline, mdiChartBar, mdiPulse, mdiClipboardListOutline, mdiSkull, mdiUndo, mdiBookOpenPageVariant, mdiLightbulbOutline, mdiFileDocumentEditOutline } from "@mdi/js";
import { UserServices } from "shared/services";
import { getVersion } from "shared/utils";

import styles from "./styles.module.scss";

interface MenuItemProps {
	icon: string;
	label: string;
	path: string;
	disabled?: boolean;
	isActive?: () => boolean;
	onClick?: () => void;
	color?: string;
}

const menuItems: Array<MenuItemProps> = [
	{
		icon: mdiViewDashboard,
		label: "Dashboard",
		path: "/backoffice",
		isActive: () => /^\/backoffice\/?$/gi.test(window.location.pathname),
	},
	{
		icon: mdiScriptTextOutline,
		label: "Logs do Sistema",
		path: "/backoffice/system-logs",
		isActive: () => /^\/backoffice\/system-logs/gi.test(window.location.pathname),
	},
	{
		icon: mdiPuzzleOutline,
		label: "Biblioteca de MCPs",
		path: "/backoffice/mcp-library",
		isActive: () => /^\/backoffice\/mcp-library/gi.test(window.location.pathname),
	},
	{
		icon: mdiRobotOutline,
		label: "Biblioteca de Membros",
		path: "/backoffice/member-library",
		isActive: () => /^\/backoffice\/member-library/gi.test(window.location.pathname),
	},
	{
		icon: mdiChartBar,
		label: "Dashboard de Consumo",
		path: "/backoffice/consumption",
		isActive: () => /^\/backoffice\/consumption/gi.test(window.location.pathname),
	},
	{
		icon: mdiPulse,
		label: "Saúde das Filas",
		path: "/backoffice/queues",
		isActive: () => /^\/backoffice\/queues/gi.test(window.location.pathname),
	},
	{
		icon: mdiClipboardListOutline,
		label: "Audit Log MCP",
		path: "/backoffice/mcp-audit",
		isActive: () => /^\/backoffice\/mcp-audit/gi.test(window.location.pathname),
	},
	{
		icon: mdiSkull,
		label: "Dead Letter Queue",
		path: "/backoffice/dlq",
		isActive: () => /^\/backoffice\/dlq/gi.test(window.location.pathname),
	},
	{
		icon: mdiUndo,
		label: "Compensations",
		path: "/backoffice/compensations",
		isActive: () => /^\/backoffice\/compensations/gi.test(window.location.pathname),
	},
	{
		icon: mdiBookOpenPageVariant,
		label: "Knowledge Base",
		path: "/backoffice/knowledge-base",
		isActive: () => /^\/backoffice\/knowledge-base/gi.test(window.location.pathname),
	},
	{
		icon: mdiLightbulbOutline,
		label: "DevBot Proposals",
		path: "/backoffice/devbot-proposals",
		isActive: () => /^\/backoffice\/devbot-proposals/gi.test(window.location.pathname),
	},
	// Prompts dos Agentes — oculto em HML por solicitacao. A rota continua
	// existindo (acesso direto via URL) mas o item nao aparece no menu lateral.
	...(process.env.REACT_APP_ENVIRONMENT === "hml"
		? []
		: [
				{
					icon: mdiFileDocumentEditOutline,
					label: "Prompts dos Agentes",
					path: "/backoffice/prompts",
					isActive: () => /^\/backoffice\/prompts/gi.test(window.location.pathname),
				} as MenuItemProps,
			]),
	{ icon: mdiAccountCog, label: "Gestão de Clientes", path: "/backoffice/clients", disabled: true },
	{ icon: mdiConsole, label: "Execução de Scripts", path: "/backoffice/scripts", disabled: true },
	{ icon: mdiCurrencyUsd, label: "Faturamento Global", path: "/backoffice/billing", disabled: true },
	{ icon: mdiLifebuoy, label: "Suporte Técnico", path: "/backoffice/support", disabled: true },
];

const MenuItem: React.FC<MenuItemProps> = ({ icon, label, path, disabled, isActive, onClick, color }) => {
	const location = useLocation();
	return useMemo(
		() => (
			<Link
				href={path}
				disabled={disabled}
				onClick={
					onClick
						? (e) => {
								e.preventDefault();
								onClick();
							}
						: undefined
				}
				className={clsx(styles.navItem, isActive?.() && styles.active)}
				style={{ "--color": color } as React.CSSProperties}
			>
				<div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
					<Icon
						path={icon}
						size={1}
					/>
					{label}
				</div>
			</Link>
		),
		[location.pathname, disabled],
	);
};

export const BackofficePage: React.FC = () => {
	return (
		<MainLayout
			sidebar={
				<ListSidebar>
					<aside className={styles.sidebar}>
						<div className={styles.sidebarTitle}>Backoffice</div>
						<nav className={styles.nav}>
							{menuItems.map((item, index) => (
								<MenuItem
									key={index}
									{...item}
								/>
							))}
							<div
								style={{
									height: "auto",
									width: "100%",
									flex: 1,
								}}
							/>
							<div className={styles.versionInfo}>VERSÃO: {getVersion()}</div>
						</nav>
					</aside>
				</ListSidebar>
			}
		>
			<div className={styles.contentContainer}>
				<div className={styles.mainContent}>
					<Routes>
						<Route
							path="/"
							element={<HomePage />}
						/>
						<Route
							path="/system-logs"
							element={<SystemLogsPage />}
						/>
						<Route
							path="/mcp-library/*"
							element={<McpLibraryPage />}
						/>
						<Route
							path="/member-library/*"
							element={<MemberLibraryPageRouter />}
						/>
						<Route
							path="/consumption"
							element={<ConsumptionPage />}
						/>
						<Route
							path="/queues"
							element={<QueueHealthPage />}
						/>
						<Route
							path="/mcp-audit"
							element={<McpAuditPage />}
						/>
						<Route
							path="/dlq"
							element={<DeadLetterJobsPage />}
						/>
						<Route
							path="/compensations"
							element={<CompensationsPage />}
						/>
						<Route
							path="/knowledge-base"
							element={<KnowledgeBaseBackofficePage />}
						/>
						<Route
							path="/devbot-proposals"
							element={<DevBotProposalsPage />}
						/>
						<Route
							path="/prompts"
							element={<PromptsPage />}
						/>
						<Route
							path="*"
							element={<Error404Page />}
						/>
					</Routes>
				</div>
			</div>
		</MainLayout>
	);
};
